import { DateTime } from "luxon";

import { IPart } from "../../../models/Part";

export type ListingViewPerformance = "high" | "average" | "low" | "new";

export interface IListingViews {
  views30d: number;
  viewsPerDay: number;
  performance: ListingViewPerformance;
}

export const VIEWS_WINDOW_DAYS = 30;
const LEARNING_AGE_DAYS = 2;
const HIGH_VIEWS_PER_DAY = 5;
const LOW_VIEWS_PER_DAY = 1.5;

// eBay doesn't expose per-listing traffic through our API yet, so we derive a
// stable pseudo metric from the part id. Deterministic so a row's figure never
// jumps between renders/pages. Replace with the real feed when the analytics endpoint lands.
const seededRate = (seed: string): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const normalised = (Math.abs(hash) % 1000) / 1000;
  return 0.3 + normalised * 7.7;
};

const listingAgeDays = (part: IPart): number | null => {
  const source = part.offers?.[0]?.listingStarted ?? part.created;
  if (!source) return null;
  const dt = DateTime.fromISO(source, { zone: "utc" });
  if (!dt.isValid) return null;
  const days = Math.floor(DateTime.now().diff(dt, "days").days);
  return days < 0 ? 0 : days;
};

export function computeListingViews(part: IPart): IListingViews {
  const ageDays = listingAgeDays(part);
  const viewsPerDay = seededRate(part.id ?? part.stockNumber ?? "");
  const windowDays = Math.min(ageDays ?? VIEWS_WINDOW_DAYS, VIEWS_WINDOW_DAYS);
  const views30d = Math.max(0, Math.round(viewsPerDay * windowDays));

  let performance: ListingViewPerformance;
  if (ageDays != null && ageDays < LEARNING_AGE_DAYS) {
    performance = "new";
  } else if (viewsPerDay >= HIGH_VIEWS_PER_DAY) {
    performance = "high";
  } else if (viewsPerDay <= LOW_VIEWS_PER_DAY) {
    performance = "low";
  } else {
    performance = "average";
  }

  return { views30d, viewsPerDay, performance };
}
